import type { Point, Rect } from './camera';

/** Oriented box: centre, size, rotation in degrees (clockwise on screen, y down) around the centre. */
export type OBB = { cx: number; cy: number; w: number; h: number; rotation: number };

const RAD = Math.PI / 180;

/** Angle in (-180, 180]. */
export function normalizeAngle(deg: number): number {
  const a = ((((deg + 180) % 360) + 360) % 360) - 180;
  return a === -180 ? 180 : a;
}

function toWorld(o: OBB, lx: number, ly: number): Point {
  const c = Math.cos(o.rotation * RAD);
  const s = Math.sin(o.rotation * RAD);
  return { x: o.cx + lx * c - ly * s, y: o.cy + lx * s + ly * c };
}

/** Corners in order: top-left, top-right, bottom-right, bottom-left (before rotation). */
export function obbCorners(o: OBB): Point[] {
  const hw = o.w / 2;
  const hh = o.h / 2;
  return [toWorld(o, -hw, -hh), toWorld(o, hw, -hh), toWorld(o, hw, hh), toWorld(o, -hw, hh)];
}

export function obbContainsPoint(o: OBB, p: Point, tolerance = 0): boolean {
  // Stryker disable next-line UnaryOperator: cos is even, cos(-a) = cos(a) (equivalent mutant).
  const c = Math.cos(-o.rotation * RAD);
  const s = Math.sin(-o.rotation * RAD);
  const dx = p.x - o.cx;
  const dy = p.y - o.cy;
  const lx = dx * c - dy * s;
  const ly = dx * s + dy * c;
  return Math.abs(lx) <= o.w / 2 + tolerance && Math.abs(ly) <= o.h / 2 + tolerance;
}

export function obbBounds(o: OBB): Rect {
  const cs = obbCorners(o);
  const xs = cs.map((p) => p.x);
  const ys = cs.map((p) => p.y);
  return { minX: Math.min(...xs), minY: Math.min(...ys), maxX: Math.max(...xs), maxY: Math.max(...ys) };
}

/** Separating-axis test between an oriented box and an axis-aligned rectangle. */
export function obbIntersectsRect(o: OBB, r: Rect): boolean {
  const rectCorners: Point[] = [
    { x: r.minX, y: r.minY }, { x: r.maxX, y: r.minY }, { x: r.maxX, y: r.maxY }, { x: r.minX, y: r.maxY },
  ];
  const obbPts = obbCorners(o);
  const c = Math.cos(o.rotation * RAD);
  const s = Math.sin(o.rotation * RAD);
  const axes: Point[] = [{ x: 1, y: 0 }, { x: 0, y: 1 }, { x: c, y: s }, { x: -s, y: c }];
  for (const a of axes) {
    const pa = obbPts.map((p) => p.x * a.x + p.y * a.y);
    const pb = rectCorners.map((p) => p.x * a.x + p.y * a.y);
    if (Math.max(...pa) < Math.min(...pb) || Math.max(...pb) < Math.min(...pa)) return false;
  }
  return true;
}

export function unionRects(rs: Rect[]): Rect | null {
  if (rs.length === 0) return null;
  return rs.reduce((u, r) => ({
    minX: Math.min(u.minX, r.minX), minY: Math.min(u.minY, r.minY),
    maxX: Math.max(u.maxX, r.maxX), maxY: Math.max(u.maxY, r.maxY),
  }));
}
